<template>
  <!-- todo: this will need to be abstracted more, but putting it off for this round -->
  <div
    role="dialog"
    aria-labelledby="directoryDetailsTitle"
    aria-describedby="directoryDetailsContent"
    class="h-full max-h-[745px]"
  >
    <!-- todo: this sort of classing with the component isolation is not ideal,
        we need module css imports or more flexible card component but I don't have
        time to think through the complexity this week -->
    <div>
      <div class="px-8 py-4">
        <div class="tgbDirectoryBeforeTitle">
          <slot name="beforeTitle" />
        </div>

        <ul class="tgbDirectoryDetailsList grid grid-cols-2 gap-4">
          <!-- Production Name -->
          <li class="grid-col-span-2">
            <h2 class="tgbDirectoryDetailsTitle">{{ details.title }}</h2>
          </li>

          <!-- Production Owner -->
          <!-- Hide this section if user is only on one team -->
          <li v-if="hasTeams" class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">
              Team
            </h3>

            <div
              :title="ownedBy"
              class="user-details-row"
            >
              <img
                v-if="details.ownedBy && details.ownedBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.ownedBy.profileImageUrl"
                :alt="`Profile image of ${details.ownedBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ ownedBy }}</span>
            </div>
          </li>

          <!-- Created By -->
          <li>
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">Created By</h3>

            <div
              :title="createdBy"
              class="user-details-row"
            >
              <img
                v-if="details.createdBy && details.createdBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.createdBy.profileImageUrl"
                :alt="`Profile image of ${details.createdBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ createdBy }}</span>
            </div>
          </li>

          <!-- Modified By -->
          <li>
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">Modified By</h3>

            <div
              :title="modifiedBy"
              class="user-details-row"
            >
              <img
                v-if="details.modifiedBy && details.modifiedBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.modifiedBy.profileImageUrl"
                :alt="`Profile image of ${details.modifiedBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ modifiedBy }}</span>
            </div>
          </li>

          <li>
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">Created On</h3>
            <span>{{ createdOn ? createdOn : dataUnavailableText }}</span>
          </li>

          <li>
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">Modified On</h3>
            <span>{{ modifiedOn ? modifiedOn : dataUnavailableText }}</span>
          </li>

          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">In This Production</h3>

            <AvatarList :users="connectedUsers" />
          </li>

          <!-- Outputs Section -->
          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel text-[13px] font-semibold uppercase tracking-wide text-white/60">Outputs</h3>

            <div v-if="isLoadingScreens" class="loading loading--inline inline-flex items-center justify-center">
              <i class="fas fa-spinner fa-spin"></i>
            </div>

            <div
              v-else-if="screens && screens.length"
              v-for="screen in screens"
              :key="screen._id"
              class="card production-details__screen-card"
            >
              <!-- Screen Output Status (Open / Closed) -->
              <div
                class="mr-3 h-2 w-2 rounded-full"
                :class="screen.status === 'disconnected' ? 'bg-producer-slate-danger' : 'bg-producer-slate-success'"
                :title="screen.connected ? 'Active' : 'Inactive'"
              />

              <div class="card-info">
                <div class="card-title text-sm font-medium">
                  {{ screen.title }}
                </div>
              </div>

              <div class="card-actions flex items-center gap-2">
                <!-- Copy To Clipboard -->
                <copy-clipboard
                  class="tb-btn tb-btn-text medium-em inline-flex h-9 w-9 items-center justify-center rounded-md text-producer-slate-textWeak transition hover:bg-white/10 hover:text-producer-slate-textWeak"
                  data-entity="producer-details-copyScreenLink"
                  :data-clipboard-text="`${baseUrl}/#/${screen.token}`"
                >
                  <span class="fas fa-link"></span>
                </copy-clipboard>

                <!-- Open In New Tab -->
                <a
                  class="tb-btn tb-btn-text medium-em inline-flex h-9 w-9 items-center justify-center rounded-md text-producer-slate-textWeak transition hover:bg-white/10 hover:text-producer-slate-textWeak"
                  :href="`${baseUrl}/#/${screen.token}`"
                  target="_blank"
                  data-entity="producer-details-openScreenExternal"
                >
                  <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
            </div>

            <div v-else>
              No outputs assigned to this production
            </div>
          </li>

          <!-- Tags Section -->
          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel">Tags</h3>
            <div class="tgb-tags">
              <span
                class="tgb-tags__tag"
                v-for="(tag, index) in details.tags"
                :key="index"
                >{{ tag }}</span
              >
            </div>

            <p
              v-if="!details.tags || !details.tags.length"
              class="mb-0"
            >
              This production has no associated tags.
            </p>
          </li>
        </ul>
      </div>

      <div class="sticky bottom-0 flex items-center justify-end gap-3 bg-producer-background p-4 text-right">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {format} from 'date-fns';

import CopyClipboard from '../../../js/shared/copy-clipboard/CopyClipboard.vue';
import AvatarList from '../user-avatar/AvatarList.vue';

export default {
  name: 'DirectoryDetails',

  components: {
    CopyClipboard,
    AvatarList,
  },

  props: {
    details: {
      default: false,
      type: [Object, Boolean],
    },
  },

  data() {
    return {
      dataUnavailableText: 'No Data',
      dateFormat: 'MM/dd/yyyy p',
      tags: [],
      tagInput: '',
      defaultAvatar: './producer/assets/img/profile_placeholder.png',
      baseUrl: process.env.LIVE_URL,
    };
  },

  created() {
    this.$store.dispatch('getScreens', this.owner);
  },

  computed: {
    ...mapGetters(['hasTeams']),

    connectedUsers() {
      return this.$store.state.clients.byProduction[this.details._id];
    },

    owner() {
      return this.details.owner;
    },

    isLoadingScreens() {
      return this.$store.state.screens.isLoading[this.owner];
    },

    screens() {
      return this.$store.getters.getScreensByProduction(this.details._id);
    },

    createdOn() {
      if ('created' in this.details) {
        return format(new Date(this.details.created), this.dateFormat);
      }

      return null;
    },

    modifiedOn() {
      if ('modified' in this.details) {
        return format(new Date(this.details.modified), this.dateFormat);
      }

      return null;
    },

    ownedBy() {
      return this.details?.ownedBy?.displayName || 'N/A';
    },

    createdBy() {
      return this.details?.createdBy?.displayName || 'N/A';
    },

    modifiedBy() {
      return this.details?.modifiedBy?.displayName || 'N/A';
    },
  },
};
</script>

<style lang="scss">
.tgbDirectoryDetailsList {
  font-size: 0.875rem;
  padding: 0;
  margin: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: 0.5rem;

  > li {
    &.grid-col-span-2 {
      grid-column: span 2 / span 2;
    }
    margin-bottom: 1.5rem;
  }
}

.tgbDirectoryDetailsLabel {
  font-size: 0.75rem;
  display: block;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.1em;
  margin-bottom: 0.25rem;
}

.tgbDirectoryDetailsTitle {
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
  word-break: break-all;
}

.tgbUserIconInline {
  width: 1.75rem;
  height: 1.75rem;
  margin-right: 1rem;
  border-radius: 100%;
}

.user-details-row {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.production-details__screen-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  background: #252525;
  box-shadow: 0 3px 0.25rem 0 rgba(0, 0, 0, .14), 0 3px 3px -2px rgba(0, 0, 0, .12), 0 1px 0.5rem 0 rgba(0, 0, 0, .2);
}
</style>
